import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { LoyaltyService } from '../api/LoyaltyService';

const LoyaltyContext = createContext(null);

function LoyaltyProvider({ user, children }) {
    // Raw `/loyalty/me` payload (`{ eligible, agbAccepted, tier, qualifyingPoints, pointsToNextTier,
    // nextTierName, currentCreditAmountCents, history }`). `null` = logged out / not yet loaded.
    const [loyalty, setLoyalty] = useState(null);

    // A request that resolved after a newer one was already issued (login/logout/account switch,
    // or a manual refresh() firing while a previous one is still in flight) must not be allowed to
    // overwrite the newer data with stale data. cancel() alone is not enough: it is a no-op once the
    // XHR has already settled, so a request that resolved just before the identity changed could
    // still run its queued .then() and write a stale balance against the new identity. This counter
    // discards any response whose id no longer matches the latest request issued.
    const requestIdRef = useRef(0);

    // The live user, read at call time rather than captured. A deferred caller — e.g. a booking's
    // success handler that fires refresh() after the create round-trip — may run after the user has
    // logged out; a stale `user` closure would then fetch /loyalty/me for a guest. Reading the ref
    // keeps refresh() a true no-op once the user is gone, and lets refresh() stay a stable callback.
    const userRef = useRef(user);
    userRef.current = user;

    const refresh = useCallback(() => {
        if (!userRef.current) return undefined;
        requestIdRef.current += 1;
        const id = requestIdRef.current;
        const request = LoyaltyService.getMyLoyalty();
        request
            .then((data) => {
                if (id === requestIdRef.current) {
                    setLoyalty(data);
                }
            })
            .catch(() => {});
        return request;
    }, []);

    useEffect(() => {
        setLoyalty(null);
        // Invalidate any in-flight response from the previous identity, even if there is no new
        // fetch to replace it with (e.g. logout).
        requestIdRef.current += 1;
        if (!user) return undefined;
        const request = refresh();
        return () => {
            if (request) request.cancel();
        };
    }, [user, refresh]);

    // Optimistic local patch after the server has already confirmed — AgbConsentModal awaits
    // acceptAgb() before calling onAccepted, so no refetch is needed here. Bumping the request id
    // first discards any /loyalty/me response still in flight: one issued before acceptance would
    // carry agbAccepted:false and, arriving late, would otherwise clobber this patch and reopen the
    // consent dialog.
    const markAgbAccepted = useCallback(() => {
        requestIdRef.current += 1;
        setLoyalty((prev) => (prev ? { ...prev, agbAccepted: true } : prev));
    }, []);

    const eligible = Boolean(loyalty?.eligible);
    const tierName = loyalty?.eligible ? (loyalty.tier?.name ?? null) : null;
    const needsAgb = Boolean(loyalty?.eligible && !loyalty.agbAccepted);

    const value = useMemo(
        () => ({ loyalty, refresh, markAgbAccepted, eligible, tierName, needsAgb }),
        [loyalty, refresh, markAgbAccepted, eligible, tierName, needsAgb],
    );

    return <LoyaltyContext.Provider value={value}>{children}</LoyaltyContext.Provider>;
}

function useLoyalty() {
    const context = useContext(LoyaltyContext);

    if (context === null) {
        throw new Error('useLoyalty must be used within a LoyaltyProvider');
    }

    return context;
}

export { LoyaltyProvider, useLoyalty };
